// Mocks for /api/secure/me/records/*

import { delay } from './index'

const STORAGE_KEY = 'patientPortal.mock.records.v1'

function readStore() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
  } catch (e) {
    return {}
  }
}
function writeStore(s) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch (e) {
    /* ignore */
  }
}

function seedFor(profile) {
  if (!profile) return { prescriptions: [], notes: [], documents: [], receipts: [] }
  const isSelf = profile.relation === 'SELF'
  const day = 24 * 60 * 60 * 1000
  const now = Date.now()
  const prescriptions = isSelf
    ? [
        {
          id: 'rx_1',
          eventId: 'evt_self_past_1',
          practitionerId: 'prac_dr_meera',
          practitionerName: 'Dr. Meera Iyer',
          practitionerSpecialty: 'Therapist',
          issuedAt: new Date(now - 7 * day).toISOString(),
          summary: 'Continue Sertraline 50mg daily',
          printable: true,
        },
      ]
    : [
        {
          id: 'rx_mom_1',
          eventId: 'evt_mom_past_1',
          practitionerId: 'prac_dr_rao',
          practitionerName: 'Dr. Rao',
          practitionerSpecialty: 'Endocrinology',
          issuedAt: new Date(now - 30 * day).toISOString(),
          summary: 'Metformin 500mg BD; Amlodipine 5mg OD',
          printable: true,
        },
      ]
  const notes = isSelf
    ? [
        {
          id: 'note_1',
          practitionerId: 'prac_dr_meera',
          practitionerName: 'Dr. Meera Iyer',
          title: 'Sleep hygiene plan',
          body:
            'Lights out by 11pm. Phone on do-not-disturb 30 min before. Keep a "tomorrow list" by the bed. Aim for 7 hours.',
          sharedAt: new Date(now - 7 * day).toISOString(),
          eventId: 'evt_self_past_1',
        },
      ]
    : []
  const receipts = isSelf
    ? [
        {
          id: 'rcpt_1',
          eventId: 'evt_self_past_1',
          practitionerName: 'Dr. Meera Iyer',
          description: 'Therapy session',
          amountMinor: 250000,
          currency: 'INR',
          paidAt: new Date(now - 7 * day).toISOString(),
          downloadUrl: '#',
          shareUrl: '#',
          type: 'STANDARD',
        },
      ]
    : [
        {
          id: 'rcpt_mom_1',
          eventId: 'evt_mom_past_1',
          practitionerName: 'Dr. Rao',
          description: 'Endocrine consultation',
          amountMinor: 150000,
          currency: 'INR',
          paidAt: new Date(now - 30 * day).toISOString(),
          downloadUrl: '#',
          shareUrl: '#',
          type: 'STANDARD',
        },
      ]
  return { prescriptions, notes, documents: [], receipts }
}

function ensure(profile) {
  const store = readStore()
  if (!store[profile.id]) {
    store[profile.id] = seedFor(profile)
    writeStore(store)
  }
  return { store, prof: store[profile.id] }
}

export async function listPrescriptions(profile) {
  await delay()
  if (!profile) return { items: [] }
  const { prof } = ensure(profile)
  return { items: prof.prescriptions, page: 1, pageSize: 20, totalItems: prof.prescriptions.length, hasMore: false }
}

export async function listNotes(profile) {
  await delay()
  if (!profile) return { items: [] }
  const { prof } = ensure(profile)
  return { items: prof.notes, page: 1, pageSize: 20, totalItems: prof.notes.length, hasMore: false }
}

export async function listSharedDocuments(profile) {
  await delay()
  if (!profile) return { items: [] }
  const items = [
    {
      id: 'shared-1',
      practitionerName: 'Dr. Gaurav Shankar',
      clinicBrandName: 'Shruti Neuro Centre',
      fileName: 'diet-plan.pdf',
      mimeType: 'application/pdf',
      sizeBytes: 184320,
      caption: 'Follow this for the next four weeks.',
      sharedAt: '2026-07-08 09:12:00',
    },
  ]
  return { items, page: 1, pageSize: 20, totalItems: items.length, hasMore: false }
}

export async function openSharedDocument() {
  await delay()
  return null
}

export async function listDocuments(profile) {
  await delay()
  if (!profile) return { items: [] }
  const { prof } = ensure(profile)
  return { items: prof.documents, page: 1, pageSize: 20, totalItems: prof.documents.length, hasMore: false }
}

export async function uploadDocument(profile, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  const doc = {
    id: `doc_${Date.now()}`,
    fileName: body.fileName,
    mimeType: body.mimeType || 'application/octet-stream',
    sizeBytes: body.sizeBytes || 0,
    downloadUrl: body.assetUrl,
    category: body.category || 'OTHER',
    description: body.description || null,
    uploadedAt: new Date().toISOString(),
    sharedWithPractitionerIds: [],
  }
  prof.documents.unshift(doc)
  store[profile.id] = prof
  writeStore(store)
  return doc
}

export async function shareDocument(profile, documentId, body) {
  await delay()
  if (!profile) return null
  const { store, prof } = ensure(profile)
  const idx = prof.documents.findIndex((d) => d.id === documentId)
  if (idx === -1) return null
  const shared = new Set(prof.documents[idx].sharedWithPractitionerIds || [])
  if (body?.revoke) shared.delete(body.practitionerId)
  else shared.add(body.practitionerId)
  prof.documents[idx].sharedWithPractitionerIds = Array.from(shared)
  store[profile.id] = prof
  writeStore(store)
  return prof.documents[idx]
}

export async function deleteDocument(profile, documentId) {
  await delay()
  if (!profile) return
  const { store, prof } = ensure(profile)
  prof.documents = prof.documents.filter((d) => d.id !== documentId)
  store[profile.id] = prof
  writeStore(store)
}

export async function listReceipts(profile) {
  await delay()
  if (!profile) return { items: [] }
  const { prof } = ensure(profile)
  return { items: prof.receipts, page: 1, pageSize: 20, totalItems: prof.receipts.length, hasMore: false }
}

export function __resetRecordsMock() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
}
